/**
 * React wrapper for Bryntum FieldFilterPickerGroup
 */
import React, { RefObject } from 'react';
import { AlignSpec, Base, CollectionFilterConfig, Container, ContainerItemConfig, ContainerLayoutConfig, FieldFilterPicker, FieldFilterPickerConfig, FieldFilterPickerGroup, FieldOption, Layout, MaskConfig, MenuItemConfig, Model, Rectangle, Scroller, ScrollerConfig, Store, TabConfig, TooltipConfig, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumFieldFilterPickerGroupProps =  {
     // Configs only
    addFilterButtonText?        : string
    adopt?                      : HTMLElement|string
    align?                      : AlignSpec|string
    anchor?                     : boolean
    ariaDescription?            : string
    ariaLabel?                  : string
    autoUpdateRecord?           : boolean
    bubbleEvents?               : object
    callOnFunctions?            : boolean
    canDeleteFilter?            : (filter: any) => boolean
    canManageFilter?            : (filter: any) => boolean
    centered?                   : boolean
    config?                     : object
    constrainTo?                : HTMLElement|Widget|Rectangle
    contentElementCls?          : string|object
    dateFormat?                 : string
    defaultBindProperty?        : string
    defaultFocus?               : (widget: Widget) => boolean
    defaults?                   : object
    dock?                       : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?                  : boolean|{
        handleSelector?: string
    }
    fields?                     : Record<string, FieldOption>
    filters?                    : Partial<CollectionFilterConfig>[]
    floating?                   : boolean
    getFieldFilterPickerConfig? : () => Partial<FieldFilterPickerConfig>
    hideAnimation?              : boolean|object
    hideWhenEmpty?              : boolean
    htmlCls?                    : string|object
    ignoreParentReadOnly?       : boolean
    itemCls?                    : string
    lazyItems?                  : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Widget[]
    limitToProperty?            : string
    listeners?                  : object
    localeClass?                : typeof Base
    localizable?                : boolean
    localizableProperties?      : string[]
    maskDefaults?               : Partial<MaskConfig>
    masked?                     : boolean|string|Partial<MaskConfig>
    monitorResize?              : boolean
    namedItems?                 : Record<string, Partial<ContainerItemConfig>>
    operators?                  : object
    owner?                      : Widget
    positioned?                 : boolean
    preventTooltipOnTouch?      : boolean
    relayStoreEvents?           : boolean
    ripple?                     : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?                : ShadowRoot
    scrollAction?               : 'hide'|'realign'|null
    showAddFilterButton?        : boolean
    showAnimation?              : boolean|object
    showTooltipWhenDisabled?    : boolean
    store?                      : Store
    tab?                        : boolean|Partial<TabConfig>
    tag?                        : string
    textAlign?                  : 'left'|'center'|'right'|'start'|'end'
    textContent?                : boolean
    title?                      : string
    triggerChangeOnInput?       : boolean
    ui?                         : string|object
    weight?                     : number

     // Configs and properties
    alignSelf?           : string
    appendTo?            : HTMLElement|string
    cls?                 : string|object
    content?             : string
    dataset?             : object
    disabled?            : boolean
    extraData?           : string|number|boolean|object
    flex?                : number|string
    height?              : number|string
    hidden?              : boolean
    html?                : string|((widget: Widget) => string)
    id?                  : string
    insertBefore?        : HTMLElement|string
    insertFirst?         : HTMLElement|string
    items?               : (Partial<ContainerItemConfig>|Partial<MenuItemConfig>|Widget)[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>>|Widget[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>|boolean|null>
    layout?              : Layout|string|Partial<ContainerLayoutConfig>
    layoutStyle?         : object
    margin?              : number|string
    maxHeight?           : string|number
    maximizeOnMobile?    : number|string
    maxWidth?            : string|number
    minHeight?           : string|number
    minWidth?            : string|number
    readOnly?            : boolean
    record?              : Model
    scrollable?          : Scroller|boolean|Partial<ScrollerConfig>
    strictRecordMapping? : boolean
    tooltip?             : string|Partial<TooltipConfig>
    width?               : number|string
    x?                   : number
    y?                   : number

     // Properties only
    anchorSize?      : number[]
    isSettingValues? : boolean
    isValid?         : boolean
    type?            : string
    value?           : Partial<CollectionFilterConfig>[]
    values?          : Record<string, object>

     // Events
    onBeforeAddFilter? : ((event: { source: FieldFilterPicker, filter: Partial<CollectionFilterConfig> }) => boolean|void)|string
    onBeforeDestroy?   : ((event: { source: Base }) => void)|string
    onBeforeHide?      : ((event: { source: Widget }) => boolean|void)|string
    onBeforeSetRecord? : ((event: { source: Container, record: Model }) => void)|string
    onBeforeShow?      : ((event: { source: Widget|any }) => Promise<boolean|void>)|string
    onCatchAll?        : ((event: {[key: string]: any, type: string}) => void)|string
    onChange?          : ((event: { source: FieldFilterPickerGroup, filters: Partial<CollectionFilterConfig>[], validFilters: Partial<CollectionFilterConfig>[] }) => void)|string
    onDestroy?         : ((event: { source: Base }) => void)|string
    onFocusIn?         : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?        : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onHide?            : ((event: { source: Widget }) => void)|string
    onPaint?           : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onReadOnly?        : ((event: { readOnly: boolean }) => void)|string
    onRecompose?       : (() => void)|string
    onResize?          : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onShow?            : ((event: { source: Widget }) => void)|string

}

export class BryntumFieldFilterPickerGroup extends React.Component<BryntumFieldFilterPickerGroupProps> {

    static instanceClass = FieldFilterPickerGroup;

    static instanceName = 'FieldFilterPickerGroup';

    processWidgetContent = processWidgetContent;

    static configNames = [
        'addFilterButtonText',
        'adopt',
        'align',
        'anchor',
        'ariaDescription',
        'ariaLabel',
        'autoUpdateRecord',
        'bubbleEvents',
        'callOnFunctions',
        'canDeleteFilter',
        'canManageFilter',
        'centered',
        'config',
        'constrainTo',
        'contentElementCls',
        'dateFormat',
        'defaultBindProperty',
        'defaultFocus',
        'defaults',
        'dock',
        'draggable',
        'fields',
        'filters',
        'floating',
        'getFieldFilterPickerConfig',
        'hideAnimation',
        'hideWhenEmpty',
        'htmlCls',
        'ignoreParentReadOnly',
        'itemCls',
        'lazyItems',
        'limitToProperty',
        'listeners',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maskDefaults',
        'masked',
        'monitorResize',
        'namedItems',
        'operators',
        'owner',
        'positioned',
        'preventTooltipOnTouch',
        'relayStoreEvents',
        'ripple',
        'rootElement',
        'scrollAction',
        'showAddFilterButton',
        'showAnimation',
        'showTooltipWhenDisabled',
        'store',
        'tab',
        'tag',
        'textAlign',
        'textContent',
        'title',
        'triggerChangeOnInput',
        'ui',
        'weight'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'appendTo',
        'cls',
        'content',
        'dataset',
        'disabled',
        'extraData',
        'flex',
        'height',
        'hidden',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'items',
        'layout',
        'layoutStyle',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'minHeight',
        'minWidth',
        'onBeforeAddFilter',
        'onBeforeDestroy',
        'onBeforeHide',
        'onBeforeSetRecord',
        'onBeforeShow',
        'onCatchAll',
        'onChange',
        'onDestroy',
        'onFocusIn',
        'onFocusOut',
        'onHide',
        'onPaint',
        'onReadOnly',
        'onRecompose',
        'onResize',
        'onShow',
        'readOnly',
        'record',
        'scrollable',
        'strictRecordMapping',
        'tooltip',
        'width',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'isSettingValues',
        'isValid',
        'type',
        'value',
        'values'
    ];

    // Component instance
    instance!: FieldFilterPickerGroup;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumFieldFilterPickerGroupProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-field-filter-picker-group-container`;
        return (
            <div className={className} ref={(element) => (this.element = element!)}></div>
        );

    }
}
